/**
 * Shared Popover Styles
 * Standard styling based on "Post melden" popover design
 * Use these styles across all popovers/modals for consistent UI
 */

// Container style for all popovers
export const popoverContainerStyle = {
  width: '356px',
  maxWidth: '90vw',
  background: 'var(--bg-primary)',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: 'var(--shadow-color)',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
};

// Wider container for forms with more fields (marketplace edit, etc.)
export const popoverContainerWideStyle = {
  ...popoverContainerStyle,
  width: '400px'
};

// Header/title style
export const popoverHeaderStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  margin: 0,
  color: 'var(--text-primary)'
};

// Label style for form fields
export const popoverLabelStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '16px',
  color: 'var(--text-primary)',
  margin: 0
};

// Input/Select base style
export const popoverInputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 400,
  fontSize: '12px',
  boxSizing: 'border-box',
  color: 'var(--text-primary)'
};

// Textarea style
export const popoverTextareaStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  resize: 'none',
  boxSizing: 'border-box',
  minHeight: '80px',
  height: 'auto',
  overflow: 'hidden',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)'
};

// Select/dropdown style with custom chevron
export const popoverSelectStyle = {
  ...popoverInputStyle,
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23878787\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '16px'
};

// Button container style
export const popoverButtonContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  gap: '12px'
};

// Cancel button style (outlined)
export const popoverCancelButtonStyle = {
  width: '141px',
  height: '36px',
  boxSizing: 'border-box',
  background: 'var(--bg-primary)',
  border: '1px solid var(--brand-primary)',
  borderRadius: '8px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  color: 'var(--brand-primary)',
  cursor: 'pointer'
};

// Submit/action button style (filled)
export const popoverSubmitButtonStyle = {
  width: '141px',
  height: '36px',
  background: 'var(--brand-primary-gradient)',
  border: 'none',
  borderRadius: '8px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  color: '#FFFFFF',
  cursor: 'pointer'
};

// Delete button style (filled red)
export const popoverDeleteButtonStyle = {
  width: '141px',
  height: '36px',
  background: '#CC0605',
  border: 'none',
  borderRadius: '8px',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  color: '#FFFFFF',
  cursor: 'pointer'
};

// Disabled button modifier
export const getDisabledStyle = (isDisabled) => ({
  opacity: isDisabled ? 0.5 : 1,
  cursor: isDisabled ? 'not-allowed' : 'pointer'
});

// Get submit button style with disabled state
export const getSubmitButtonStyle = (isDisabled) => ({
  ...popoverSubmitButtonStyle,
  background: 'var(--brand-primary-gradient)',
  opacity: isDisabled ? 0.4 : 1,
  cursor: isDisabled ? 'not-allowed' : 'pointer'
});

// Get cancel button style with disabled state
export const getCancelButtonStyle = (isDisabled) => ({
  ...popoverCancelButtonStyle,
  ...getDisabledStyle(isDisabled)
});

// Get delete button style with disabled state
export const getDeleteButtonStyle = (isDisabled) => ({
  ...popoverDeleteButtonStyle,
  background: isDisabled ? '#999' : '#CC0605',
  cursor: isDisabled ? 'not-allowed' : 'pointer'
});

// Confirmation message style
// Issue #110: Changed textAlign from 'center' to 'left'
export const popoverMessageStyle = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: '14px',
  color: 'var(--text-primary)',
  margin: 0,
  textAlign: 'left'
};
